import { useRecoilValue } from 'recoil';
import {
    RoiReadModel,
    StructureSetReadModel,
} from '~/domain/readmodels/StructureSetReadModel';
import { primaryImageState } from '~/state/atoms/image/imageAtoms';
import {
    bulkRoiSelectionActiveState,
    selectedROIState,
    selectedRoiIsPointTypeState,
} from '~/state/atoms/structureSet/roiAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';

export interface StructureToolGating {
    selectedStructureSet: StructureSetReadModel | undefined;
    selectedRoiNumber: number;
    selectedRoi: RoiReadModel | undefined;
    isPointRoi: boolean;
    isTilted: boolean;
    /** The structure set is approved (rejects creating or modifying ROIs). */
    isStructureSetApproved: boolean;
    /** The structure set or the selected ROI is approved. */
    isSelectionApproved: boolean;
    isBulkSelectionActive: boolean;
    /** A structure set with at least one ROI is loaded. */
    hasStructures: boolean;
    /** A concrete ROI is selected (may be a point ROI). */
    hasSelectedRoi: boolean;
    /**
     * In-place contour editing of the selected ROI is unavailable — drawing
     * tools (brush/line/erase/deform) and slice operations (clear/interpolate)
     * all share this gate.
     */
    selectedRoiContourEditDisabled: boolean;
    /**
     * Structure-set-level editing is unavailable — operations that need a
     * writable, non-point structure set (boolean/margin) and undo/redo.
     */
    structureSetEditDisabled: boolean;
}

/**
 * Single source of the selection/approval/image flags that gate ROI-editing
 * toolbar controls, so each control composes its disabled state from shared
 * primitives instead of re-reading the same atoms.
 */
export function useStructureToolGating(): StructureToolGating {
    const selectedStructureSet = useRecoilValue(currentStructureSetState);
    const selectedRoiNumber = useRecoilValue(selectedROIState);
    const isPointRoi = useRecoilValue(selectedRoiIsPointTypeState);
    const isBulkSelectionActive = useRecoilValue(bulkRoiSelectionActiveState);
    const primaryImage = useRecoilValue(primaryImageState);

    const selectedRoi = selectedStructureSet?.roiArray.find(
        (roi) => roi.roiNumber === selectedRoiNumber
    );

    const isTilted = !!primaryImage?.firstSlice.tilted;
    const hasStructures =
        !!selectedStructureSet && selectedStructureSet.roiArray.length > 0;
    const hasSelectedRoi = hasStructures && selectedRoiNumber !== -1;
    const isStructureSetApproved = !!selectedStructureSet?.isApproved;
    const isSelectionApproved =
        isStructureSetApproved || !!selectedRoi?.isApproved;

    return {
        selectedStructureSet,
        selectedRoiNumber,
        selectedRoi,
        isPointRoi,
        isTilted,
        isStructureSetApproved,
        isSelectionApproved,
        isBulkSelectionActive,
        hasStructures,
        hasSelectedRoi,
        selectedRoiContourEditDisabled:
            !hasSelectedRoi ||
            isPointRoi ||
            isTilted ||
            isSelectionApproved ||
            isBulkSelectionActive,
        structureSetEditDisabled:
            !hasStructures || isPointRoi || isBulkSelectionActive,
    };
}
